import { v4 as uuidv4 } from 'uuid';

interface MessageSegment {
  id: string;
  type: 'text' | 'preview';
  content: string;
}

/**
 * Maps New Admin routes to Agent Chat expected format
 */
function mapRoutesToNavigation(routes: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(routes).map(([urlName, url]) => [urlName, { url }]),
  );
}

/**
 * Parses AI message content by extracting preview blocks delimited by <preview> tags
 * and raw text sections, maintaining their original order
 *
 * @param content - The message content that may contain <preview> XML tags
 * @returns Array of segments with their type and content
 */
function parseMessageContent(content: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  const previewRegex = /<preview>([\s\S]*?)<\/preview>/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = previewRegex.exec(content)) !== null) {
    const textBefore = content.slice(lastIndex, match.index).trim();
    if (textBefore) {
      segments.push({
        type: 'text',
        id: uuidv4(),
        content: textBefore,
      });
    }

    segments.push({
      id: uuidv4(),
      type: 'preview',
      content: match[1].trim(),
    });

    lastIndex = previewRegex.lastIndex;
  }

  const textAfter = content.slice(lastIndex).trim();
  if (textAfter) {
    segments.push({
      id: uuidv4(),
      type: 'text',
      content: textAfter,
    });
  }

  if (segments.length === 0) {
    segments.push({
      id: uuidv4(),
      type: 'text',
      content: content.trim(),
    });
  }

  return segments;
}

/**
 * Checks if an AI message segment is only a relative navigation link
 */
const isNavigationLinkSegment = (segment: string): boolean => {
  // Match the pattern [text](href)
  const linkPattern = /^\s*\[([^\]]+)\]\(([^)]+)\)\s*$/;

  const match = segment.match(linkPattern);
  if (!match) return false;

  const href = match[2];
  // Only relative links starting with '/'
  return href.startsWith('/') && !href.startsWith('#');
};

export { mapRoutesToNavigation, parseMessageContent, isNavigationLinkSegment };
